// HU-01: entrar con el número de celular (código por SMS), con Google o con correo y contraseña.

import { useEffect, useRef, useState, type FormEvent } from "react";
import { Navigate, useSearchParams } from "react-router";
import {
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  RecaptchaVerifier,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPhoneNumber,
  signInWithPopup,
  signInWithRedirect,
  type ConfirmationResult,
} from "firebase/auth";
import { correoDeDocumento } from "@porteria/core";
import { auth } from "../firebase";
import { rutaDeVuelta, useSesion } from "../sesion";
import { Boton, Campo, Cargando, Tarjeta, useAvisos } from "../ui";
import { Icono } from "../iconos";

const ERRORES: Record<string, string> = {
  "auth/invalid-credential": "Correo o contraseña incorrectos.",
  "auth/invalid-email": "Revisa el correo.",
  "auth/user-not-found": "No hay una cuenta con ese correo.",
  "auth/wrong-password": "Correo o contraseña incorrectos.",
  "auth/email-already-in-use": "Ya existe una cuenta con ese correo. Entra con tu contraseña.",
  "auth/weak-password": "La contraseña debe tener al menos 8 caracteres.",
  "auth/too-many-requests": "Demasiados intentos. Espera unos minutos.",
  "auth/network-request-failed": "Sin conexión. Revisa tu internet.",
  "auth/invalid-phone-number": "Revisa el número: son 10 dígitos y empieza por 3.",
  "auth/invalid-verification-code": "El código no coincide. Revísalo en el mensaje que te llegó.",
  "auth/code-expired": "El código venció. Pide uno nuevo.",
  "auth/quota-exceeded": "No pudimos enviar el código ahora. Intenta en unos minutos o entra con Google.",
  "auth/operation-not-allowed": "El acceso con celular aún no está activo. Entra con Google o con tu correo.",
  "auth/popup-closed-by-user": "",
  "auth/cancelled-popup-request": "",
};

const mensaje = (e: unknown) => {
  const codigo = (e as { code?: string }).code ?? "";
  return codigo in ERRORES ? ERRORES[codigo] : "No pudimos iniciar sesión. Intenta de nuevo.";
};

/** Sin "@" es un número de documento: las cuentas que crea la administración entran así (adminCrearPersonal). */
const usuarioDe = (texto: string) => (texto.includes("@") ? texto.trim() : correoDeDocumento(texto));

/**
 * Navegador dentro de otra app (Instagram, Facebook, TikTok, vista web de Android): ahí Google no deja iniciar
 * sesión. WhatsApp abre los enlaces en el navegador normal y la app de Android usa Chrome, así que no cuentan.
 */
const enNavegadorInterno = () => /FBAN|FBAV|FB_IAB|Instagram|TikTok|musical_ly|Snapchat|Line\//i.test(navigator.userAgent);

function AvisoNavegadorInterno() {
  const [copiado, setCopiado] = useState(false);
  if (!enNavegadorInterno()) return null;
  const copiar = () =>
    navigator.clipboard
      ?.writeText(location.href)
      .then(() => setCopiado(true))
      .catch(() => {});
  return (
    <Tarjeta className="alerta">
      <strong>Abre este enlace en Chrome o Safari</strong>
      <p className="chico">
        Estás dentro de otra app y aquí Google no deja entrar. Toca los tres puntos de arriba y elige “Abrir en el navegador”, o copia el enlace y
        pégalo en Chrome o Safari. También puedes entrar con tu celular aquí mismo.
      </p>
      <Boton variante="secundario" onClick={copiar}>
        {copiado ? "Enlace copiado" : "Copiar enlace"}
      </Boton>
    </Tarjeta>
  );
}

/** Entrar con el número de celular: llega un código por SMS. Colombia (+57) por defecto. */
function ConCelular() {
  const [error, setError] = useState<string | null>(null);
  const alError = (e: unknown) => setError(mensaje(e) || null);
  const [numero, setNumero] = useState("");
  const [codigo, setCodigo] = useState("");
  const [confirmacion, setConfirmacion] = useState<ConfirmationResult | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const verificador = useRef<RecaptchaVerifier | null>(null);
  useEffect(() => () => verificador.current?.clear(), []);
  const digitos = numero.replace(/\D/g, "");
  const internacional = numero.trim().startsWith("+") ? `+${digitos}` : `+57${digitos}`;

  const enviar = async (e: FormEvent) => {
    e.preventDefault();
    setOcupado(true);
    setError(null);
    try {
      verificador.current ??= new RecaptchaVerifier(auth, "recaptcha-celular", { size: "invisible" });
      setConfirmacion(await signInWithPhoneNumber(auth, internacional, verificador.current));
    } catch (err) {
      verificador.current?.clear();
      verificador.current = null;
      alError(err);
    } finally {
      setOcupado(false);
    }
  };
  const confirmar = async (e: FormEvent) => {
    e.preventDefault();
    if (!confirmacion) return;
    setOcupado(true);
    setError(null);
    try {
      await confirmacion.confirm(codigo.trim());
    } catch (err) {
      alError(err);
    } finally {
      setOcupado(false);
    }
  };

  return confirmacion ? (
    <form className="pila" onSubmit={confirmar}>
      <Campo etiqueta="Código que te llegó por SMS" ayuda={`Lo enviamos al ${internacional}.`}>
        <input
          className="pin-entrada"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          value={codigo}
          onChange={(e) => setCodigo(e.target.value.replace(/\D/g, "").slice(0, 6))}
        />
      </Campo>
      {error && <p className="aviso-linea aviso-error">{error}</p>}
      <Boton type="submit" grande ocupado={ocupado} disabled={codigo.length !== 6}>
        Entrar
      </Boton>
      <button type="button" className="boton boton-texto" onClick={() => setConfirmacion(null)}>
        Cambiar el número
      </button>
    </form>
  ) : (
    <form className="pila" onSubmit={enviar}>
      <Campo etiqueta="Tu número de celular" ayuda="Te enviamos un código por SMS. No necesitas contraseña.">
        <input type="tel" inputMode="tel" autoComplete="tel-national" placeholder="300 123 4567" value={numero} onChange={(e) => setNumero(e.target.value)} maxLength={16} />
      </Campo>
      <div id="recaptcha-celular" />
      {error && <p className="aviso-linea aviso-error">{error}</p>}
      <Boton type="submit" grande ocupado={ocupado} disabled={digitos.length < 10}>
        Enviarme el código
      </Boton>
    </form>
  );
}

export default function Entrar() {
  const { cuenta } = useSesion();
  const [parametros] = useSearchParams();
  const { avisar } = useAvisos();
  const [modo, setModo] = useState<"entrar" | "crear" | "olvide">("entrar");
  const [correo, setCorreo] = useState("");
  const [clave, setClave] = useState("");
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => setError(null), [modo]);

  if (cuenta === undefined) return <Cargando />;
  if (cuenta) return <Navigate to={rutaDeVuelta(parametros)} replace />;

  const conGoogle = async () => {
    setOcupado(true);
    setError(null);
    const proveedor = new GoogleAuthProvider();
    proveedor.setCustomParameters({ prompt: "select_account" });
    try {
      await signInWithPopup(auth, proveedor);
    } catch (e) {
      const codigo = (e as { code?: string }).code;
      if (codigo === "auth/popup-blocked" || codigo === "auth/operation-not-supported-in-this-environment") {
        await signInWithRedirect(auth, proveedor);
        return;
      }
      setError(mensaje(e) || null);
    } finally {
      setOcupado(false);
    }
  };

  const conCorreo = async (e: FormEvent) => {
    e.preventDefault();
    setOcupado(true);
    setError(null);
    try {
      if (modo === "olvide") {
        await sendPasswordResetEmail(auth, correo.trim());
        avisar("Te enviamos un correo para crear una contraseña nueva.");
        setModo("entrar");
      } else if (modo === "crear") {
        if (clave.length < 8) throw { code: "auth/weak-password" };
        await createUserWithEmailAndPassword(auth, correo.trim(), clave);
      } else {
        await signInWithEmailAndPassword(auth, modo === "entrar" ? usuarioDe(correo) : correo.trim(), clave);
      }
    } catch (err) {
      setError(mensaje(err) || null);
    } finally {
      setOcupado(false);
    }
  };

  return (
    <div className="entrada">
      <header className="entrada-portada">
        <div className="marca">
          <img src="/icono.svg" alt="" />
          <h1>Portería a la Puerta</h1>
          <p>Tu paquete de la portería hasta tu puerta, sin bajar.</p>
          <div className="entrada-ventajas">
            <span>
              <Icono nombre="paquete" tamano={16} /> Aviso al llegar
            </span>
            <span>
              <Icono nombre="moto" tamano={16} /> Entrega en minutos
            </span>
            <span>
              <Icono nombre="escudo" tamano={16} /> Con PIN seguro
            </span>
          </div>
          <a className="entrada-conocer" href="/conocer/">
            <Icono nombre="libro" tamano={16} /> ¿Qué es Portería a la Puerta? <strong>Conócela</strong> <span aria-hidden>→</span>
          </a>
        </div>
      </header>
      <main className="entrada-cuerpo">
        <AvisoNavegadorInterno />
        <Tarjeta>
          <h2>Entrar con tu celular</h2>
          <ConCelular />
          <div className="separador-texto">o</div>
          <Boton variante="secundario" grande onClick={conGoogle} ocupado={ocupado}>
            <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden>
              <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
              <path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
              <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" />
              <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
            </svg>
            Continuar con Google
          </Boton>
          <div className="separador-texto">o con tu correo</div>
          <h2>{modo === "crear" ? "Crear cuenta con correo" : modo === "olvide" ? "Recuperar contraseña" : "Entrar con correo"}</h2>
          <form className="pila" onSubmit={conCorreo}>
            <Campo
              etiqueta={modo === "entrar" ? "Correo o documento" : "Correo"}
              ayuda={
                modo === "entrar"
                  ? "Si la administración te creó la cuenta sin correo, escribe tu número de documento."
                  : modo === "olvide"
                    ? "Si entras con tu documento, pídele una clave nueva a la administración."
                    : undefined
              }
            >
              <input
                type={modo === "entrar" ? "text" : "email"}
                inputMode="email"
                autoCapitalize="none"
                autoComplete="username"
                required
                value={correo}
                onChange={(e) => setCorreo(e.target.value)}
              />
            </Campo>
            {modo !== "olvide" && (
              <Campo etiqueta="Contraseña" ayuda={modo === "crear" ? "Mínimo 8 caracteres." : undefined}>
                <input
                  type="password"
                  autoComplete={modo === "crear" ? "new-password" : "current-password"}
                  required
                  value={clave}
                  onChange={(e) => setClave(e.target.value)}
                />
              </Campo>
            )}
            {error && <p className="aviso-linea aviso-error">{error}</p>}
            <Boton type="submit" grande ocupado={ocupado}>
              {modo === "crear" ? "Crear cuenta" : modo === "olvide" ? "Enviar correo" : "Entrar"}
            </Boton>
          </form>
          <div className="entre chico">
            {modo === "entrar" ? (
              <>
                <button className="boton boton-texto" onClick={() => setModo("crear")}>Crear cuenta</button>
                <button className="boton boton-texto" onClick={() => setModo("olvide")}>Olvidé mi contraseña</button>
              </>
            ) : (
              <button className="boton boton-texto" onClick={() => setModo("entrar")}>Ya tengo cuenta</button>
            )}
          </div>
        </Tarjeta>
      </main>
    </div>
  );
}
